/**
 * Form input: `state` (default/error/…), `inputSize`, left/right icons, `prefixText`, `floatingLabel`, `passwordToggle`.
 * Use inside `FormField` or standalone; classes use CSS variables from `form/tokens` / `fieldStyles`.
 */
import React, { useMemo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../cn';
import { getFieldBaseStyle, getFieldColors } from './fieldStyles';
const heightVar = {
    sm: 'var(--field-height-sm, 32px)',
    default: 'var(--field-height-md, 40px)',
    lg: 'var(--field-height-lg, 48px)',
};
export const Input = ({ state = 'default', inputSize = 'default', leftIcon, rightIcon, prefixText, floatingLabel, passwordToggle = false, type, className, disabled, readOnly, value, defaultValue, onFocus, onBlur, ...props }) => {
    const effectiveState = disabled ? 'disabled' : readOnly ? 'readonly' : state;
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [pwVisible, setPwVisible] = useState(false);
    const hasValue = useMemo(() => {
        const v = value !== null && value !== void 0 ? value : defaultValue;
        return v != null && String(v).length > 0;
    }, [value, defaultValue]);
    const showPwToggle = passwordToggle && (type === 'password' || type == null);
    const effectiveType = showPwToggle ? (pwVisible ? 'text' : 'password') : type;
    const focusColors = getFieldColors(effectiveState);
    const borderWidth = effectiveState === 'error' || effectiveState === 'success' || focused
        ? 'var(--stroke-active, 1.5px)'
        : 'var(--stroke-normal, 1px)';
    const borderColor = focused
        ? focusColors.border
        : hovered
            ? 'var(--form-border-hover, rgba(11,18,32,0.36))'
            : 'var(--form-border, rgba(11,18,32,0.22))';
    const ringShadow = focused && effectiveState !== 'readonly' && effectiveState !== 'disabled'
        ? `0 0 0 3px ${focusColors.ring}`
        : 'none';
    const wrapperStyle = {
        ...getFieldBaseStyle(effectiveState),
        borderStyle: 'solid',
        borderWidth,
        borderColor,
        borderRadius: 'var(--radius-md, 8px)',
        boxShadow: ringShadow,
        height: heightVar[inputSize],
        opacity: effectiveState === 'disabled' ? 0.6 : 1,
        transition: 'all 150ms ease',
    };
    const inputStyle = {
        height: '100%',
        background: 'transparent',
        border: 0,
        outline: 'none',
        width: '100%',
        fontSize: 'var(--text-body, 14px)',
        color: 'inherit',
    };
    const placeholderColor = 'var(--form-placeholder, rgba(11,18,32,0.38))';
    return (React.createElement("div", { className: cn('relative w-full', floatingLabel && 'pt-2') },
        floatingLabel ? (React.createElement("div", { className: cn('pointer-events-none absolute left-3 top-1 z-10 text-[length:var(--text-meta,11px)] font-medium', focused || hasValue
                ? 'text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]'
                : 'text-[color:var(--form-placeholder,rgba(11,18,32,0.38))]') }, floatingLabel)) : null,
        React.createElement("div", { className: cn('flex w-full items-center gap-2 px-3', className), style: wrapperStyle, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false) },
            prefixText != null ? (React.createElement("div", { className: "shrink-0 text-[length:var(--text-body,14px)] text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]", "aria-hidden": true }, prefixText)) : null,
            leftIcon != null ? (React.createElement("div", { className: "shrink-0 text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]", "aria-hidden": true }, leftIcon)) : null,
            React.createElement("input", { ...props, type: effectiveType, disabled: effectiveState === 'disabled' ? true : undefined, readOnly: effectiveState === 'readonly' ? true : readOnly, value: value, defaultValue: defaultValue, onFocus: (e) => {
                    setFocused(true);
                    onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
                }, onBlur: (e) => {
                    setFocused(false);
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
                }, className: "min-w-0 flex-1 placeholder:opacity-100", style: {
                    ...inputStyle,
                    paddingTop: floatingLabel ? '10px' : undefined,
                    cursor: effectiveState === 'disabled' ? 'not-allowed' : undefined,
                    '::placeholder': { color: placeholderColor },
                } }),
            showPwToggle ? (React.createElement("button", { type: "button", className: "shrink-0 rounded-md p-1 text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))] hover:bg-black/5", onClick: () => setPwVisible((v) => !v), disabled: effectiveState === 'disabled', "aria-label": pwVisible ? 'Hide password' : 'Show password', tabIndex: effectiveState === 'disabled' ? -1 : 0 }, pwVisible ? React.createElement(EyeOff, { className: "h-4 w-4" }) : React.createElement(Eye, { className: "h-4 w-4" }))) : rightIcon != null ? (React.createElement("div", { className: "shrink-0 text-[color:var(--form-fg-muted,rgba(11,18,32,0.66))]", "aria-hidden": true }, rightIcon)) : null)));
};
export default Input;
